/**
 * ============================================================================
 * ESCROW RELEASE COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for releasing escrow funds to seller
 */

import React, { useState } from 'react';
import { useEscrow } from '../../hooks/useEscrow';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Textarea from '../common/Textarea';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';

/**
 * Escrow Release Component
 * @param {Object} props
 * @param {string} props.escrowId - Escrow ID
 * @param {Object} props.escrow - Escrow object
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const EscrowRelease = ({ escrowId, escrow, onSuccess }) => {
  const { releaseEscrow, loading } = useEscrow(escrowId);
  const { showSuccess, showError } = useToast();
  const { formatCurrency, language } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState('');

  const handleRelease = async () => {
    try {
      await releaseEscrow(escrowId, {
        release_notes: releaseNotes.trim() || undefined,
      });
      showSuccess(
        language === 'fr'
          ? 'Fonds libérés avec succès!'
          : 'Funds released successfully!'
      );
      setShowModal(false);
      setReleaseNotes('');
      if (onSuccess) onSuccess();
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de la libération' : 'Failed to release funds')
      );
    }
  };

  if (!escrow || escrow.status !== 'funded') {
    return null;
  }

  return (
    <>
      <Card variant="outlined" className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl shadow-lg">
              ✅
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-gray-900 dark:text-white mb-1">
                {language === 'fr' ? 'Libérer les fonds' : 'Release Funds'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'fr'
                  ? 'Libérez les fonds en escrow au vendeur'
                  : 'Release escrow funds to seller'}
              </p>
            </div>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Montant à libérer' : 'Amount to Release'}:
              </span>
              <span className="text-xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
              </span>
            </div>
          </div>
          <Button
            variant="gradient"
            fullWidth
            size="lg"
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 shadow-xl hover:shadow-2xl transition-all"
          >
            {language === 'fr' ? 'Libérer les fonds' : 'Release Funds'} →
          </Button>
        </div>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setReleaseNotes('');
        }}
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-xl">
              ✅
            </div>
            <span className="text-xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              {language === 'fr' ? 'Libérer les fonds' : 'Release Funds'}
            </span>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Amount Display */}
          <Card variant="outlined" className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {language === 'fr' ? 'Montant à libérer' : 'Amount to Release'}
              </p>
              <p className="text-3xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
              </p>
            </div>
          </Card>

          {/* Warning */}
          <Card variant="outlined" className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-bold text-yellow-800 dark:text-yellow-200 mb-1">
                  {language === 'fr' ? 'Attention' : 'Warning'}
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {language === 'fr'
                    ? 'Cette action libérera tous les fonds en escrow au vendeur. Cette action est irréversible.'
                    : 'This action will release all escrow funds to the seller. This action is irreversible.'}
                </p>
              </div>
            </div>
          </Card>

          {/* Release Notes */}
          <Textarea
            label={language === 'fr' ? 'Notes de libération (optionnel)' : 'Release Notes (optional)'}
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
            placeholder={
              language === 'fr'
                ? 'Ajoutez des notes sur cette libération...'
                : 'Add notes about this release...'
            }
            rows={3}
          />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowModal(false);
                setReleaseNotes('');
              }}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleRelease}
              loading={loading}
              className="bg-gradient-to-r from-green-600 to-emerald-600 shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Confirmer la libération' : 'Confirm Release'} →
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EscrowRelease;
